import { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  FileText, 
  Plus, 
  Search, 
  Trash2, 
  Copy, 
  Edit3, 
  Calendar, 
  Tag,
  MoreVertical,
  Printer
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

export const TemplateLibrary = ({ 
  rubrics, 
  selectedRubric,
  editingRubric, 
  onSelect, 
  onEdit, 
  onDelete, 
  onDuplicate, 
  onCreateNew,
  onGenerateSheet,
  loading 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rubricToDelete, setRubricToDelete] = useState(null);

  const filteredRubrics = rubrics.filter(rubric => 
    rubric.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    rubric.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleDeleteClick = (rubric, e) => {
    e.stopPropagation();
    setRubricToDelete(rubric);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (rubricToDelete) {
      await onDelete(rubricToDelete.id);
      setDeleteDialogOpen(false);
      setRubricToDelete(null);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header section with proper padding */}
      <div className="p-4 border-b border-border flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-lg">Templates</h2>
          <Button
            variant="outline"
            size="sm"
            onClick={onCreateNew}
            data-testid="create-new-rubric-btn"
            className="rounded-xl"
          >
            <Plus className="w-4 h-4 mr-1" />
            New
          </Button>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="search-templates-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search templates..."
            className="pl-9 rounded-xl"
          />
        </div>
      </div>

      {/* Scrollable rubric list */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-2">
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">
              Loading templates...
            </div>
          ) : filteredRubrics.length === 0 ? (
            <div className="text-center py-8 px-2">
              {searchQuery ? (
                <div>
                  <p className="text-muted-foreground">No templates match your search.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <img
                    src="https://images.pexels.com/photos/6893325/pexels-photo-6893325.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=200&w=300"
                    alt="Empty workspace"
                    className="mx-auto rounded-2xl empty-state-image w-40 h-28 object-cover"
                  />
                  <div>
                    <p className="text-muted-foreground font-medium">No rubrics yet</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Create your first rubric to get started.
                    </p>
                  </div>
                  <Button
                    onClick={onCreateNew}
                    data-testid="create-first-rubric-btn"
                    className="rounded-xl"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Rubric
                  </Button>
                </div>
              )}
            </div>
          ) : (
            filteredRubrics.map((rubric) => {
              const isEditing = editingRubric?.id === rubric.id;
              const isSelected = selectedRubric?.id === rubric.id;
              
              return (
                <Card
                  key={rubric.id}
                  data-testid={`rubric-card-${rubric.id}`}
                  className={`cursor-pointer transition-all duration-200 hover:shadow-md ${
                    isEditing
                      ? 'ring-2 ring-primary border-primary bg-primary/5'
                      : isSelected
                      ? 'ring-1 ring-primary/50 border-primary/50'
                      : 'border-border hover:border-primary/30'
                  }`}
                  onClick={() => onSelect(rubric)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" strokeWidth={1.5} />
                          <h3 className="font-medium text-sm truncate">{rubric.title}</h3>
                        </div>
                        {isEditing && (
                          <div className="mt-1 text-xs text-primary font-medium">
                            Currently editing
                          </div>
                        )}
                        <div className="flex items-center gap-2 mt-1.5 text-xs text-muted-foreground">
                          <Tag className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{rubric.category}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                          <Calendar className="w-3 h-3 flex-shrink-0" />
                          <span>{formatDate(rubric.created_at)}</span>
                        </div>
                        <div className="mt-1.5 text-xs text-muted-foreground">
                          {rubric.number_of_levels}-point | {rubric.domains?.length || 0} domains
                        </div>
                      </div>
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 flex-shrink-0">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem 
                            onClick={(e) => { e.stopPropagation(); onEdit(rubric); }}
                            data-testid={`edit-rubric-${rubric.id}`}
                          >
                            <Edit3 className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={(e) => { e.stopPropagation(); onGenerateSheet(rubric); }}
                            data-testid={`generate-rubric-${rubric.id}`}
                          >
                            <Printer className="w-4 h-4 mr-2" />
                            Generate Sheet
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={(e) => { e.stopPropagation(); onDuplicate(rubric.id); }}
                            data-testid={`duplicate-rubric-${rubric.id}`}
                          >
                            <Copy className="w-4 h-4 mr-2" />
                            Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={(e) => handleDeleteClick(rubric, e)}
                            data-testid={`delete-rubric-${rubric.id}`}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Rubric</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{rubricToDelete?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              className="rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TemplateLibrary;
